import type { components } from "@linkhr/api-client/core";
import { useMePermissions } from "../api/useMePermissions";
import { gateFor, ownsCompany } from "../shell/gate";
import { codesOf, type LedgerWindow } from "./books";

export interface LedgerAccess {
  company: components["schemas"]["CapabilityCompany"] | undefined;
  /** Holds the permission code in this company. */
  can: (code: string) => boolean;
  /** Link Accounting is read-only for the workspace: every write is off. */
  readOnly: boolean;
  /** The account owner (grant source OWNER). */
  owns: boolean;
  /** An auditor's months, or null when not limited. */
  window: LedgerWindow | null;
}

/**
 * This company as the signed-in person holds it. The company layout has
 * already read the permissions and let the company in.
 */
export function useLedgerAccess(companyId: string): LedgerAccess {
  const data = useMePermissions().data;
  const gate = data === undefined ? null : gateFor(data, companyId);
  const company = gate !== null && "company" in gate ? gate.company : undefined;
  const codes = company === undefined ? [] : codesOf(company);
  return {
    company,
    can: (code) => codes.includes(code),
    readOnly: gate?.kind === "read_only",
    owns: company !== undefined && ownsCompany(company),
    window: company?.ledgerWindow ?? null,
  };
}
